import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import "./UnconfirmedBatches.css";

export default function UnconfirmedBatches() {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    let isMounted = true;

    const loadBatches = async () => {
      try {
        const { data } = await axios.get("/api/unconfirmed-batches");
        if (isMounted) {
          setBatch(data?.batch || null);
        }
      } catch (err) {
        console.error("Failed to load unconfirmed batches:", err);
        if (isMounted) {
          setError("Не удалось загрузить данные");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBatches();

    return () => {
      isMounted = false;
    };
  }, []);

  const totalItems = useMemo(
    () => batch?.items?.length || 0,
    [batch]
  );

  const updateItemStatus = (reservationId, status) => {
    setBatch((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((item) =>
        String(item.reservationId) === String(reservationId)
          ? { ...item, status, updatedAt: new Date().toISOString() }
          : item
      );
      return { ...prev, items };
    });
  };

  const handleConfirm = async (reservationId) => {
    if (!reservationId) return;
    setActionLoading((prev) => ({ ...prev, [reservationId]: "confirm" }));
    try {
      await axios.post(`/api/unconfirmed-batches/${reservationId}/confirm`);
      updateItemStatus(reservationId, "confirmed");
    } catch (err) {
      console.error("Failed to confirm batch item:", err);
      setError("Не удалось подтвердить заявку");
    } finally {
      setActionLoading((prev) => ({ ...prev, [reservationId]: "" }));
    }
  };

  const handleCancel = async (reservationId) => {
    if (!reservationId) return;
    setActionLoading((prev) => ({ ...prev, [reservationId]: "cancel" }));
    try {
      await axios.post(`/api/unconfirmed-batches/${reservationId}/cancel`);
      updateItemStatus(reservationId, "cancelled");
    } catch (err) {
      console.error("Failed to cancel batch item:", err);
      setError("Не удалось отменить заявку");
    } finally {
      setActionLoading((prev) => ({ ...prev, [reservationId]: "" }));
    }
  };

  const normalizePhone = (raw) => {
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("8")) return `+7${digits.slice(1)}`;
    if (digits.length === 10) return `+7${digits}`;
    if (digits.length === 11 && digits.startsWith("7")) return `+${digits}`;
    if (digits.length > 0) return `+${digits}`;
    return raw;
  };

  const formatPhone = (raw) => {
    const normalized = normalizePhone(raw);
    const digits = normalized.replace(/\D/g, "");
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
    }
    return raw;
  };

  const pluralize = (count, one, few, many) => {
    const mod100 = count % 100;
    const mod10 = count % 10;
    if (mod100 >= 11 && mod100 <= 19) return many;
    if (mod10 === 1) return one;
    if (mod10 >= 2 && mod10 <= 4) return few;
    return many;
  };

  return (
    <div className="unconfirmed-batches">
      <div className="unconfirmed-batches__header">
        <div>
          <h1 className="unconfirmed-batches__title">Заявки на прозвон</h1>
          <p className="unconfirmed-batches__subtitle">
            Гости, которые не подтвердили визит
          </p>
        </div>
        <div className="unconfirmed-batches__summary">
          <span className="unconfirmed-batches__summary-label">Всего</span>
          <span className="unconfirmed-batches__summary-value">
            {totalItems}
          </span>
        </div>
      </div>

      {loading && <p className="unconfirmed-batches__text">Загрузка...</p>}
      {!loading && error && (
        <p className="unconfirmed-batches__error">{error}</p>
      )}
      {!loading && !error && !batch && (
        <p className="unconfirmed-batches__text">Пусто</p>
      )}

      {!loading && !error && batch && (
        <div className="unconfirmed-batches__list">
          <section key={batch._id} className="unconfirmed-batches__batch">
            <div className="unconfirmed-batches__batch-header">
              <h2 className="unconfirmed-batches__batch-title">
                {batch.dateLabel || batch.dateKey}
              </h2>
              <span className="unconfirmed-batches__batch-meta">
                {batch.items?.length || 0}{" "}
                {pluralize(batch.items?.length || 0, "заявка", "заявки", "заявок")}
              </span>
            </div>

            <div className="unconfirmed-batches__cards">
              {(batch.items || []).map((item) => {
                const name =
                  item?.data?.bookingName ||
                  item?.user?.firstName ||
                  "Гость";
                const username = item?.user?.username
                  ? `@${item.user.username}`
                  : "";
                const phone = item?.data?.phone || "";
                const time = item?.data?.time || "—";
                const guests = item?.data?.guests ?? "—";
                const description = item?.data?.description || "";
                const status = item?.status || "pending";
                const isConfirmed = status === "confirmed";
                const isCancelled = status === "cancelled";
                const isBusy = actionLoading[String(item.reservationId)];
                const isLocked = isConfirmed || isCancelled || !!isBusy;

                return (
                  <article
                    key={String(item.reservationId)}
                    className="unconfirmed-batches__card"
                  >
                    <div className="unconfirmed-batches__card-head">
                      <div>
                        <div className="unconfirmed-batches__name-row">
                          <div className="unconfirmed-batches__name">
                            {name}
                          </div>
                        </div>
                        {username && (
                          <div className="unconfirmed-batches__username">
                            {username}
                          </div>
                        )}
                      </div>
                      {phone ? (
                        <a
                          className="unconfirmed-batches__call"
                          href={`tel:${normalizePhone(phone)}`}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Позвонить"
                        >
                          <svg
                            className="unconfirmed-batches__call-icon"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.59a1 1 0 01-.25 1.01l-2.2 2.19z"
                              fill="currentColor"
                            />
                          </svg>
                        </a>
                      ) : (
                        <span className="unconfirmed-batches__call unconfirmed-batches__call--disabled">
                          <svg
                            className="unconfirmed-batches__call-icon"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.59a1 1 0 01-.25 1.01l-2.2 2.19z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                      )}
                    </div>

                      <div className="unconfirmed-batches__meta">
                        {phone && (
                          <>
                            <a
                              className="unconfirmed-batches__phone"
                              href={`tel:${normalizePhone(phone)}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {formatPhone(phone)}
                            </a>
                            <span className="unconfirmed-batches__meta-sep">·</span>
                          </>
                        )}
                        <span>
                          Время: <b>{time}</b>
                        </span>
                        <span className="unconfirmed-batches__meta-sep">·</span>
                        <span>
                          Гости: <b>{guests}</b>
                        </span>
                      </div>

                      {description && (
                        <div className="unconfirmed-batches__note">
                          {description}
                        </div>
                      )}

                      {(isConfirmed || isCancelled) && (
                        <div
                          className={`unconfirmed-batches__status ${
                            isConfirmed
                              ? "unconfirmed-batches__status--confirmed"
                              : "unconfirmed-batches__status--cancelled"
                          }`}
                        >
                          {isConfirmed ? "Подтверждено" : "Отменено"}
                        </div>
                      )}

                      {!isConfirmed && !isCancelled && (
                        <div className="unconfirmed-batches__actions">
                          <button
                            type="button"
                            className="unconfirmed-batches__button unconfirmed-batches__button--confirm"
                            onClick={() => handleConfirm(item.reservationId)}
                            disabled={isLocked}
                          >
                            Подтвердить
                          </button>
                          <button
                            type="button"
                            className="unconfirmed-batches__button unconfirmed-batches__button--cancel"
                            onClick={() => handleCancel(item.reservationId)}
                            disabled={isLocked}
                          >
                            Отменить
                          </button>
                        </div>
                      )}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
